import React from 'react';
import { usePlayer } from './PlayerContext';
import { formatTime } from './format';
import { formatDate } from '../utils/dateFormatter';
import './player.css';

/**
 * Dashboard hero card for the newest episode across all schedules (T2.4).
 * Reads playback state/actions from PlayerContext directly — no onPlay prop.
 *
 * `episode` comes from Dashboard's existing RSS scan:
 *   { id, title, audioUrl, pubDate, durationSeconds, duration,
 *     scheduleName, sources, episodeFormat }
 */
function LatestEpisodeCard({ episode }) {
  const { currentlyPlaying, isPlaying, play, togglePause } = usePlayer();

  if (!episode) return null;

  const isCurrent = currentlyPlaying?.id === episode.id;
  const playingThis = isCurrent && isPlaying;
  const durationLabel =
    episode.durationSeconds != null ? formatTime(episode.durationSeconds) : null;

  const handleToggle = () => {
    if (isCurrent) {
      togglePause();
    } else {
      play(episode).catch((err) => {
        console.error('❌ Error playing latest episode:', err);
      });
    }
  };

  return (
    <div className="latest-episode-card">
      <button
        type="button"
        className="latest-episode-card__toggle"
        aria-label={playingThis ? `Pause ${episode.title}` : `Play ${episode.title}`}
        onClick={handleToggle}
        disabled={!episode.audioUrl}
        title={episode.audioUrl ? undefined : 'Audio file not available'}
      >
        {playingThis ? '⏸️' : '▶️'}
      </button>
      <div className="latest-episode-card__info">
        <div className="latest-episode-card__label">⏰ Latest episode</div>
        <div className="latest-episode-card__title">{episode.title}</div>
        <div className="latest-episode-card__meta">
          {episode.scheduleName && <span>📅 {episode.scheduleName}</span>}
          {episode.pubDate && <span>🗓️ {formatDate(episode.pubDate)}</span>}
          {durationLabel && <span>⏱️ {durationLabel}</span>}
          {(episode.episodeFormat || 'article') === 'conversational' && (
            <span>🎙️ Two-host conversation</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default LatestEpisodeCard;
